/**
 * Manages the selection of objects within the scene.
 * @category Experience
 * @subcategory ObjectManagement
 */
class SelectionManager {
  /**
   * Creates an instance of SelectionManager.
   * @param {Object} experience - The experience object containing the scene, event hub, and other resources.
   */
  constructor(experience) {
    this.experience = experience;
    this.experience.selectedObjects = new Set();
    this.selectedObjects = this.experience.selectedObjects; // Reference the Set
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;
    this.setupEventListeners();
  }

  cleanup() {
    this.eventHub.off("singleSelect", this.handleSingleSelect.bind(this));
    this.eventHub.off("modifierSelect", this.handleModifierSelect.bind(this));
    this.eventHub.off("singleDragSelect", this.handleSingleDragSelect.bind(this));
    this.eventHub.off("modifierDragSelect", this.handleModifierDragSelect.bind(this));
    this.eventHub.off("multiSelect", this.handleMultiSelect.bind(this));
    this.eventHub.off("modifierMultiSelect", this.handleModifierMultiSelect.bind(this));
    this.eventHub.off("clearAll", this.clearAll.bind(this));
    this.eventHub.off("inquireSelectedObjects", this.inquireSelectedObjects.bind(this));
    this.eventHub.off("deleteSelectedObjects", this.handleDeleteSelectedObjects.bind(this));

    this.selectedObjects.clear(); // Clear selected objects
    console.log("SelectionManager cleanup complete: Event listeners removed and selection cleared.");
  }

  /**
   * Sets up event listeners for various selection-related events.
   */
  setupEventListeners() {
    this.eventHub.on("singleSelect", this.handleSingleSelect.bind(this));
    this.eventHub.on("modifierSelect", this.handleModifierSelect.bind(this));
    this.eventHub.on(
      "singleDragSelect",
      this.handleSingleDragSelect.bind(this)
    );
    this.eventHub.on(
      "modifierDragSelect",
      this.handleModifierDragSelect.bind(this)
    );
    this.eventHub.on("clearAll", this.clearAll.bind(this));
    this.eventHub.on("multiSelect", this.handleMultiSelect.bind(this));
    this.eventHub.on(
      "modifierMultiSelect",
      this.handleModifierMultiSelect.bind(this)
    );
    this.eventHub.on(
      "inquireSelectedObjects",
      this.inquireSelectedObjects.bind(this)
    );
    this.eventHub.on(
      "deleteSelectedObjects",
      this.handleDeleteSelectedObjects.bind(this)
    );
    this.eventHub.listListeners();
  }

  /**
   * Handles single selection of an object. Clears the current selection if the object is not already selected.
   * @param {Object} object - The object to select or deselect.
   */
  handleSingleSelect(object) {
    if (this.selectedObjects.has(object)) {
      this.selectedObjects.delete(object);
    } else {
      this.selectedObjects.clear();
      this.selectedObjects.add(object);
    }
    this.updateSelection();
  }

  /**
   * Handles modifier-based selection (e.g., Ctrl or Cmd + click). Adds or removes an object from the current selection.
   * @param {Object} object - The object to select or deselect.
   */
  handleModifierSelect(object) {
    if (object) {
      if (this.selectedObjects.has(object)) {
        this.selectedObjects.delete(object);
      } else {
        this.selectedObjects.add(object);
      }
      this.updateSelection();
    }
  }

  /**
   * Handles single drag-based selection. Clears the current selection and selects a single object.
   * @param {Object} object - The object to select.
   */
  handleSingleDragSelect(object) {
    if (!this.selectedObjects.has(object)) {
      this.selectedObjects.clear();
      this.selectedObjects.add(object);
    }
    this.updateSelection();
  }

  /**
   * Handles modifier-based drag selection. Adds an object to the current selection if not already selected.
   * @param {Object} object - The object to add to the selection.
   */
  handleModifierDragSelect(object) {
    if (object) {
      if (!this.selectedObjects.has(object)) {
        this.selectedObjects.add(object);
      }
      this.updateSelection();
    }
  }

  /**
   * Handles multi-selection of objects. Clears the current selection and selects all provided objects.
   * @param {Object[]} objects - An array of objects to select.
   */
  handleMultiSelect(objects) {
    this.selectedObjects.clear();
    objects.forEach((object) => this.selectedObjects.add(object));
    this.updateSelection();
  }

  /**
   * Handles modifier-based multi-selection. Adds objects to the current selection if not already selected.
   * @param {Object[]} objects - An array of objects to add to the selection.
   */
  handleModifierMultiSelect(objects) {
    objects.forEach((object) => {
      if (!this.selectedObjects.has(object)) {
        this.selectedObjects.add(object);
      }
    });
    this.updateSelection();
  }

  /**
   * Clears all selected objects.
   */
  clearAll() {
    this.selectedObjects.clear();
    this.updateSelection();
  }

  /**
   * Handles deletion of selected objects.
   * @param {Object} param - Contains the objects to delete.
   * @param {Object[]} param.objects - An array of objects to delete.
   */
  handleDeleteSelectedObjects({ objects }) {
    if (!Array.isArray(objects)) {
      objects = [objects];
    }
    objects.forEach((object) => this.selectedObjects.delete(object));
    this.updateSelection();
  }

  /**
   * Updates the current selection and emits an event with the updated selection.
   */
  updateSelection() {
    const selectedObjectsArray = Array.from(this.selectedObjects);
    this.eventHub.emit("selectionUpdated", {
      selectedObjects: selectedObjectsArray,
    });
  }

  /**
   * Emits an event with the current selected objects.
   */
  inquireSelectedObjects() {
    const selectedObjectsArray = Array.from(this.selectedObjects);
    this.eventHub.emit("selectedObjectsInquiryResponse", {
      selectedObjects: selectedObjectsArray,
    });
  }

  /**
   * Retrieves the currently selected objects.
   * @returns {Object[]} An array of currently selected objects.
   */
  getSelectedObjects() {
    return Array.from(this.selectedObjects);
  }
}

export default SelectionManager;
